import { useRouter } from 'next/router'
import { useCallback, useEffect, useState, VFC } from 'react'
import { GET_CURRENT_COMPANY } from 'src/api/endpoints'
import { deleteKeep, postKeep } from 'src/api/keep'
import { MasterDataStore } from 'src/api/masterData'
import {
  deleteCompanySearchConditions,
  getNeedsCsv,
  toNeedsQueryUrl,
  useCompanySearchConditions,
  useNeeds,
} from 'src/api/needs'
import { usePersonaList } from 'src/api/persona'
import { updateShowMessageCountSideMessage } from 'src/api/user'
import { TipsLink } from 'src/components/Link'
import { MessageCount } from 'src/components/MessageCount'
import { MemberPageWrapper } from 'src/components/PageWrapper'
import { Pagination } from 'src/components/Pagination'
import { ProfileCard } from 'src/components/UserCard'
import {
  NeedsQueryParam,
  toNeedsQueryParam,
  toNeedsQueryParamFreeWord,
  toPersona,
} from 'src/models/Needs'
import { Persona } from 'src/models/Persona'
import { LoginUser, ROLE } from 'src/models/User'
import { truewhite } from 'src/styles/colors'
import { companyDetail, getNeedsUrl, GUIDE_LINK } from 'src/utils/routes'
import styled, { css } from 'styled-components'
import { useSWRConfig } from 'swr'
import { CompanyPersonasForm } from './CompanyPersonasForm'
import { FilterSettingModal } from './FilterSettingModal'
import { MessageCountModal } from './MessageCountModal'
import { PersonasContent } from './PersonasContent'

type NeedsLayoutProps = NeedsContentProps
export const NeedsLayout: VFC<NeedsLayoutProps> = (props) => {
  const { user } = props
  return (
    <>
      <NeedsWrapper
        user={user}
        currentPath="決裁者を探す"
        displayBreadCrumb={false}
      >
        <NeedsContent {...props} />
      </NeedsWrapper>
    </>
  )
}

type NeedsContentProps = {
  user: LoginUser
  masterData: MasterDataStore
}
const NeedsContent: VFC<NeedsContentProps> = ({ user, masterData }) => {
  const [page, setPage] = useState<number>(1)
  const [searchQueryParam, setSearchQueryParam] = useState<
    NeedsQueryParam | undefined
  >({})
  const [searchPersonaData, setSearchPersonaData] = useState<Persona>({
    id: 0,
    personaAdminAgeIds: [],
    personaBusinessObjectIds: [],
    personaEmployeeNumberIds: [],
    personaFoundedAtIds: [],
    personaPrefectureIds: [],
    personaTypeOfBusinessIds: [],
    personaCompanyBuyNeedOptionIds: [],
    personaSolutionIds: [],
    personaCapitalIds: [],
    personaPositionIds: [],
    personaRoleForAnalysisIds: [],
    refuseMessage: false,
    noMessage: false,
    isNew: false,
    isListedCompany: null,
    freeWord: null,
    minCreatedDate: null,
    maxCreatedDate: null,
    minLastSignInAt: null,
    maxLastSignInAt: null,
    personaAdminAges: [],
    personaCompanyBuyNeedOptions: [],
    personaSolutions: [],
    personaBusinessObjects: [],
    personaEmployeeNumbers: [],
    personaFoundedAts: [],
    personaPrefectures: [],
    personaTypeOfBusinesses: [],
    personaCapitals: [],
    personaPositions: [],
    personaRoleForAnalysis: [],
  })
  const [selectedPersonaData, setSelectedPersonaData] = useState<Persona>({
    id: 0,
    personaAdminAgeIds: [],
    personaBusinessObjectIds: [],
    personaEmployeeNumberIds: [],
    personaFoundedAtIds: [],
    personaPrefectureIds: [],
    personaTypeOfBusinessIds: [],
    personaCompanyBuyNeedOptionIds: [],
    personaSolutionIds: [],
    personaCapitalIds: [],
    personaPositionIds: [],
    personaRoleForAnalysisIds: [],
    refuseMessage: false,
    noMessage: false,
    isNew: false,
    isListedCompany: null,
    freeWord: null,
    minCreatedDate: null,
    maxCreatedDate: null,
    minLastSignInAt: null,
    maxLastSignInAt: null,
    personaAdminAges: [],
    personaCompanyBuyNeedOptions: [],
    personaBusinessObjects: [],
    personaEmployeeNumbers: [],
    personaFoundedAts: [],
    personaPrefectures: [],
    personaTypeOfBusinesses: [],
    personaCapitals: [],
    personaPositions: [],
    personaRoleForAnalysis: [],
    personaSolutions: [],
  })
  const [isFirst, setFirst] = useState(true)
  const [isFixed, setFixed] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [showSettingModal, setShowSettingModal] = useState(false)
  const [showMessageCountModal, setShowMessageCountModal] =
    useState<boolean>(false)

  const getCompanySearchConditions = async () => {
    await searchCompanySearchConditionsDataMutate()
  }

  const handleDeleteCompanySearchConditions = async (id: number) => {
    if (window.confirm('本当に削除しますか？')) {
      const res = await deleteCompanySearchConditions(id)
      if (res.error) alert('検索条件の削除に失敗しました')
      else {
        alert(`検索条件を削除しました`)
        await getCompanySearchConditions()
      }
    }
  }

  const toggleIsFixed = () => {
    window.scrollY > 830 ? setFixed(true) : setFixed(false)
  }

  useEffect(() => {
    window.addEventListener('scroll', toggleIsFixed)
    return () => window.removeEventListener('scroll', toggleIsFixed)
  }, [])

  const { mutate } = useSWRConfig()
  const { data: needs } = useNeeds(searchQueryParam, page)
  const { data: personaDataList } = usePersonaList()
  const {
    data: searchCompanySearchConditionsData,
    mutate: searchCompanySearchConditionsDataMutate,
  } = useCompanySearchConditions()
  const router = useRouter()
  const { query } = useRouter()
  useEffect(() => {
    mutate(GET_CURRENT_COMPANY)
    let buy = 1
    if (query.buy) buy = Number(query.buy)
    setPage(buy)
    let needsQueryParam = query as unknown as NeedsQueryParam
    if (user.plan.role !== ROLE.ONLYSTORY_REFERRER) {
      needsQueryParam.min_created_date = undefined
      needsQueryParam.max_created_date = undefined
      needsQueryParam.min_last_sign_in_at = undefined
      needsQueryParam.max_last_sign_in_at = undefined
    }
    const persona = toPersona(needsQueryParam, masterData)
    setSearchPersonaData(persona)
    setSelectedPersonaData(persona)
    setSearchQueryParam(needsQueryParam)
    setFirst(false)
    onClickPagination()
  }, [query])

  useEffect(() => {
    onClickPagination()
  }, [needs])

  useEffect(() => {
    checkMessageCount()
  }, [user])

  const checkMessageCount = async () => {
    if (
      user.member &&
      user.member.id &&
      user.member.showMessageCountSideMessage > 0 &&
      !showMessageCountModal
    ) {
      await updateShowMessageCountSideMessage()
      mutate(GET_CURRENT_COMPANY)
      setShowMessageCountModal(true)
    }
  }

  const onClickPagination = async () => {
    if (router.asPath.split('#')[1])
      window.scroll({ top: 830, behavior: 'smooth' })
  }

  const searchNeeds = async (hasFreeWord: boolean) => {
    const needsQueryParam = hasFreeWord
      ? toNeedsQueryParamFreeWord(selectedPersonaData)
      : toNeedsQueryParam(selectedPersonaData)
    setPage(1)
    router.push(getNeedsUrl(toNeedsQueryUrl(needsQueryParam), 1, true))
  }

  const fetchNeedsCsv = async () => {
    const data = await getNeedsCsv(searchQueryParam, page)
    const blob = new Blob([data])
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'needs.csv'
    a.click()
    a.remove()
  }

  const onClickDeleteKeep = async (id: number) => {
    onClickKeep(id, true)
  }

  const onClickPostKeep = async (id: number) => {
    onClickKeep(id, false)
  }

  const [, updateState] = useState({})
  const forceUpdate = useCallback(() => updateState({}), [])
  const onClickKeep = async (id: number, isDelete: boolean) => {
    try {
      if (isDelete) await deleteKeep(id)
      else await postKeep(id)
      needs?.items.forEach((item) => {
        if (item.id === id) item.kept = !isDelete
      })
      forceUpdate()
    } catch (err) {
      // through
    }
  }

  return (
    <>
      <Container>
        <MessageDiv>
          <MessageDivHeader>
            <div>
              <h2>決裁者を探す</h2>
              <p>{`登録されている決裁者様を様々な条件から絞込み、
あなたがつながりたいと思う方を見つけてメッセージを送ってみましょう。`}</p>
            </div>
            {(user.plan.paid || user.plan.isAdminRole) && (
              <MessageCountTipsWrapper>
                {user.plan.paid && user.member && (
                  <MessageCount
                    currentCount={user.member.messageCount}
                    totalCount={user.member.messageTotal}
                  />
                )}
                <TipsLinkWrap>
                  <TipsLink to={GUIDE_LINK} />
                </TipsLinkWrap>
              </MessageCountTipsWrapper>
            )}
          </MessageDivHeader>
        </MessageDiv>
        {!isFirst && (
          <CompanyPersonasFormDiv isFixed={isFixed}>
            <CompanyPersonasForm
              user={user}
              masterData={masterData}
              personaData={selectedPersonaData}
              setPersonaData={setSelectedPersonaData}
              searchNeeds={searchNeeds}
              searchCompanySearchConditionsData={
                searchCompanySearchConditionsData
              }
              deleteCompanySearchConditions={
                handleDeleteCompanySearchConditions
              }
              personaDataList={personaDataList?.personas}
            />
          </CompanyPersonasFormDiv>
        )}
        <FilterPopUp>
          <FilterPopUpContainer isFixed={isFixed}>
            <FilterPopUpLeftWrapper>
              <p className="text">対象ユーザ：{needs?.totalCount}人</p>
              <span className="border"></span>
              <button
                className="confirm-open-btn"
                onClick={() => setIsConfirmOpen(!isConfirmOpen)}
              >
                検索条件を確認する
              </button>
              {isConfirmOpen && (
                <PersonaConfilmModalDiv>
                  <OuterDiv onClick={() => setIsConfirmOpen(false)} />
                  <PersonaConfilmModal>
                    <ConfirmHeader>
                      <ConfirmHeaderTitle>選択中の項目</ConfirmHeaderTitle>
                      <ConfirmCloseBtn
                        onClick={() => setIsConfirmOpen(!isConfirmOpen)}
                      >
                        ×
                      </ConfirmCloseBtn>
                    </ConfirmHeader>
                    <PersonasContent personaData={searchPersonaData} />
                  </PersonaConfilmModal>
                </PersonaConfilmModalDiv>
              )}
            </FilterPopUpLeftWrapper>
            <FilterPopUpRightWrapper>
              {user.plan.role === ROLE.ONLYSTORY_REFERRER && (
                <FilterPopUpLink onClick={() => fetchNeedsCsv()}>
                  CSV出力
                </FilterPopUpLink>
              )}
              <FilterPopUpButton
                onClick={() => window.scroll({ top: 0, behavior: 'smooth' })}
              >
                検索フィルタを再設定
              </FilterPopUpButton>
              <FilterPopUpButton onClick={() => setShowSettingModal(true)}>
                現在の検索条件を保存
              </FilterPopUpButton>
            </FilterPopUpRightWrapper>
          </FilterPopUpContainer>
        </FilterPopUp>
        <CompanyNeedsContainerDiv>
          <CompanyNeedsContainer>
            {needs &&
              needs.items.length > 0 &&
              needs.items.map((item) => (
                <CompanyNeedsItem key={item.id}>
                  <ProfileCard
                    restrictProfile={user.plan.free}
                    key={item.id}
                    user={item}
                    showDetail={() => {
                      window.open(companyDetail(item.id, true, true), '_blank')
                    }}
                    actions={[]}
                    isNeeds={true}
                    deleteKeep={onClickDeleteKeep}
                    postKeep={onClickPostKeep}
                    searchedBusinessTypeIds={
                      searchQueryParam?.type_of_business_id
                        ?.split('|')
                        .map(Number)
                        .filter((n) => !isNaN(n)) || []
                    }
                    businessTypeMasterData={masterData.businessTypes}
                  />
                </CompanyNeedsItem>
              ))}
          </CompanyNeedsContainer>
          {needs && needs.totalCount > 0 ? (
            <Pagination
              itemSize={needs.totalCount}
              setIndex={(index) => {
                setPage(index)
                router.push(
                  getNeedsUrl(toNeedsQueryUrl(searchQueryParam ?? {}), index),
                )
              }}
              forcePage={page - 1}
              onePageDisplayItems={needs.onePageDisplayItems}
            ></Pagination>
          ) : (
            <>検索条件に当てはまる企業が見つかりませんでした</>
          )}
        </CompanyNeedsContainerDiv>
      </Container>

      {showSettingModal && (
        <FilterSettingModal
          personaData={selectedPersonaData}
          close={() => setShowSettingModal(false)}
          getCompanySearchConditions={getCompanySearchConditions}
        />
      )}
      {showMessageCountModal && (
        <MessageCountModal
          close={() => setShowMessageCountModal(false)}
          user={user}
        />
      )}
    </>
  )
}
const NeedsWrapper = styled(MemberPageWrapper)`
  width: 88%;
  margin: 0px 6% 0px 6%;
`

const MessageDiv = styled.div`
  width: 100%;
  margin: 20px auto 0 auto;
  padding 0 6px;
  font-weight: 500;
  h2 {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 23px;
  }
  p {
    font-size: 14px;
    font-weight: bold;
    white-space: pre-wrap;
  }
`

const MessageDivHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const CompanyPersonasFormDiv = styled.div<{ isFixed?: boolean }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  ${({ isFixed }) =>
    isFixed &&
    css`
      margin-bottom: 160px;
    `};
`

const FilterPopUp = styled.div`
  width: 100%;
  padding: 0 6px;
`

const FilterPopUpContainer = styled.div<{ isFixed?: boolean }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 30px 60px;
  box-shadow: 3px 6px 10px rgb(59 57 72 / 25%);
  border-radius: 10px;
  margin-top: 80px;

  ${({ isFixed }) =>
    isFixed &&
    css`
      position: fixed;
      z-index: 2;
      top: 0;
      background: #fff;
      width: 87%;
    `};
`

const FilterPopUpLeftWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 14px;
  font-weight: bold;

  .text {
    color: #bbbbbb;
    margin: 0;
  }

  .border {
    height: 20px;
    border-left: 1px solid #bbbbbb;
    margin: 0 15px 0 10px;
  }

  .confirm-open-btn {
    color: #02b7b0;
    padding: 10px;
    background: none;
    border: none;
  }
`

const FilterPopUpRightWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

const FilterPopUpLink = styled.button`
  width: 200px;
  padding: 10px 0;
  border: 1px solid #02b7b0;
  background-color: ${truewhite};
  color: #02b7b0;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 3px 6px rgb(0 0 0 / 10%);
  border-radius: 5px;
  margin: 0 10px;
  outline: none;
  text-align: center;

  :hover {
    background-color: #02b7b0;
    color: ${truewhite};
  }
`

const FilterPopUpButton = styled.button`
  width: 200px;
  padding: 10px 0;
  border: 1px solid #02b7b0;
  background-color: ${truewhite};
  color: #02b7b0;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 3px 6px rgb(0 0 0 / 10%);
  border-radius: 5px;
  margin: 0 10px;
  outline: none;
  text-align: center;

  :hover {
    background-color: #02b7b0;
    color: ${truewhite};
  }
`

const Container = styled.div``

const CompanyNeedsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;

  @media screen and (max-width: 1200px) {
    justify-content: space-evenly;
  }
`

const CompanyNeedsContainerDiv = styled.div`
  padding: 3%;
`

const CompanyNeedsItem = styled.div`
  width: 389px;
  margin: 24px;
`

const PersonaConfilmModalDiv = styled.div`
  position: relative;
`

const PersonaConfilmModal = styled.div`
  position: absolute;
  transform: translate(-120px, 30px);
  width: 550px;
  max-height: 400px;
  background: ${truewhite};
  border-radius: 10px;
  box-shadow: 3px 6px 10px rgb(59 57 72 / 50%);
  padding: 20px;
  z-index: 2;

  overflow: scroll;
`

const ConfirmHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #d8d7da;
  margin-bottom: 10px;
`

const ConfirmHeaderTitle = styled.p`
  margin: 0;
  color: #02b7b0;
  font-weight: bold;
  font-size: 14px;
`

const ConfirmCloseBtn = styled.div`
  font-size: 22px;
  font-weight: bold;
  color: #02b7b0;
  cursor: pointer;
  position: relative;
  top: -8px;
`

const OuterDiv = styled.div`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
`

const MessageCountTipsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: start;
  gap: 40px;
`

const TipsLinkWrap = styled.div`
  padding: 15px 8px;
`
